import { SdkContext } from '../internal'
import type {
  AudiencePreview,
  AutomaticCampaign,
  AutomaticUpdate,
  BlockSaveResult,
  CampaignBlock,
  Campaign,
  CampaignAudience,
  CampaignBlockItem,
  CampaignDraft,
  CampaignPreview,
  CampaignQuota,
  CampaignReport,
  CampaignRun,
  CampaignStats,
  CampaignStatus,
  ScheduleInput,
  OptOut,
  OptOutScope,
  SendResult
} from '@/types/campaigns'

interface Page<T> {
  items: T[]
  pagination: { page: number; limit: number; total: number; totalPages: number } | null
}

/** F-73: e-mail campaigns, the quota, the opt-out list and the member's own e-mail preference. */
export class CampaignsResource {
  constructor(private ctx: SdkContext) {}

  /** `sentFrom`/`sentTo` (YYYY-MM-DD, inclusive, community's days): only campaigns with a send in the period. */
  async list(params: { page?: number; limit?: number; status?: CampaignStatus; sentFrom?: string; sentTo?: string } = {}): Promise<Page<Campaign>> {
    return this.ctx.raw({ httpMethod: 'GET', path: '/campaigns' }, { query: params })
  }

  async get(id: string): Promise<Campaign> {
    return this.ctx.call({ httpMethod: 'GET', path: '/campaigns/{id}' }, { id })
  }

  async create(draft: CampaignDraft): Promise<Campaign> {
    return this.ctx.call({ httpMethod: 'POST', path: '/campaigns', hasBody: true }, { body: draft })
  }

  /** `updatedAt`: the version read; another save after it answers 409 with `data.updatedAt` (the stored one). */
  async update(id: string, draft: Partial<CampaignDraft> & { updatedAt?: string; blocksUpdatedAt?: string }): Promise<Campaign> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/campaigns/{id}', hasBody: true }, { id, body: draft })
  }

  /** F-174: saves one block of the draft (423 while someone else holds it; never 409 for another block). */
  async updateBlock(id: string, blockId: string, block: CampaignBlock): Promise<BlockSaveResult> {
    return this.ctx.call<BlockSaveResult>({ httpMethod: 'PUT', path: '/campaigns/{id}/blocks/{blockId}', hasBody: true }, { id, blockId, body: { block } })
  }

  async remove(id: string): Promise<void> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/campaigns/{id}' }, { id })
  }

  async quota(): Promise<CampaignQuota> {
    return this.ctx.call({ httpMethod: 'GET', path: '/campaigns/quota' })
  }

  async optOuts(params: { page?: number; limit?: number } = {}): Promise<Page<OptOut>> {
    return this.ctx.raw({ httpMethod: 'GET', path: '/campaigns/opt-outs' }, { query: params })
  }

  async previewAudience(id: string, audience?: CampaignAudience): Promise<AudiencePreview> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/campaigns/{id}/audience-preview', hasBody: true },
      { id, body: audience ? { audience } : {} }
    )
  }

  async sendTest(id: string): Promise<{ sentTo: string }> {
    return this.ctx.call({ httpMethod: 'POST', path: '/campaigns/{id}/test', hasBody: true }, { id, body: {} })
  }

  async send(id: string, consent: boolean): Promise<SendResult> {
    return this.ctx.call({ httpMethod: 'POST', path: '/campaigns/{id}/send', hasBody: true }, { id, body: { consent } })
  }

  /** Owner/admin. `sendAt` is local text in the community time zone: the server never converts it. */
  async schedule(id: string, input: ScheduleInput): Promise<Campaign> {
    return this.ctx.call({ httpMethod: 'POST', path: '/campaigns/{id}/schedule', hasBody: true }, { id, body: input })
  }

  async pause(id: string): Promise<Campaign> {
    return this.ctx.call({ httpMethod: 'POST', path: '/campaigns/{id}/pause', hasBody: true }, { id, body: {} })
  }

  async resume(id: string): Promise<Campaign> {
    return this.ctx.call({ httpMethod: 'POST', path: '/campaigns/{id}/resume', hasBody: true }, { id, body: {} })
  }

  async cancel(id: string): Promise<Campaign> {
    return this.ctx.call({ httpMethod: 'POST', path: '/campaigns/{id}/cancel', hasBody: true }, { id, body: {} })
  }

  async runs(id: string, params: { page?: number; limit?: number } = {}): Promise<Page<CampaignRun>> {
    return this.ctx.raw({ httpMethod: 'GET', path: '/campaigns/{id}/runs' }, { id, query: params })
  }

  async report(id: string): Promise<CampaignReport> {
    return this.ctx.call({ httpMethod: 'GET', path: '/campaigns/{id}/report' }, { id })
  }

  /** The e-mail as a person would get it, rendered now. Sends nothing and spends no quota. */
  async preview(id: string): Promise<CampaignPreview> {
    return this.ctx.call({ httpMethod: 'GET', path: '/campaigns/{id}/preview' }, { id })
  }

  /** Totals, per-day series and the previous period of all sends in the last `days` days. */
  async stats(params: { days?: number } = {}): Promise<CampaignStats> {
    return this.ctx.call({ httpMethod: 'GET', path: '/campaigns/stats' }, { query: params })
  }

  /** Courses, events or products a fixed block accepts now (the same rule the save checks). */
  async blockItems(params: { type: 'product' | 'event' | 'course'; search?: string; page?: number; limit?: number }): Promise<Page<CampaignBlockItem>> {
    return this.ctx.raw({ httpMethod: 'GET', path: '/campaigns/block-items' }, { query: params })
  }

  /** F-175: a draft made from a post or article (its text, cover and a button back), audience "who can open it". */
  async fromContent(contentId: string): Promise<Campaign> {
    return this.ctx.call({ httpMethod: 'POST', path: '/campaigns/from-content', hasBody: true }, { body: { contentId } })
  }

  /** A new draft with the same content and audience. Automatic campaigns answer 409 `automaticManaged`. */
  async duplicate(id: string): Promise<Campaign> {
    return this.ctx.call({ httpMethod: 'POST', path: '/campaigns/{id}/duplicate', hasBody: true }, { id, body: {} })
  }

  /** Variables a subject or text can carry (`{{nome}}`, leaders, groups...). Older APIs answer 404. */
  async variables(): Promise<unknown> {
    return this.ctx.raw({ httpMethod: 'GET', path: '/campaigns/variables' })
  }

  async listAutomatic(): Promise<AutomaticCampaign[]> {
    return this.ctx.call({ httpMethod: 'GET', path: '/campaigns/automatic' })
  }

  async updateAutomatic(systemKey: string, input: AutomaticUpdate): Promise<AutomaticCampaign> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/campaigns/automatic/{systemKey}', hasBody: true }, { systemKey, body: input })
  }

  async restoreAutomatic(systemKey: string): Promise<AutomaticCampaign> {
    return this.ctx.call({ httpMethod: 'POST', path: '/campaigns/automatic/{systemKey}/restore', hasBody: true }, { systemKey, body: {} })
  }

  async uploadImage(file: File): Promise<{ url: string }> {
    const fd = new FormData()
    fd.append('image', file)
    return this.ctx.call({ httpMethod: 'POST', path: '/upload/campaigns/image', hasBody: true }, { body: fd })
  }

  async deleteImage(url: string): Promise<void> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/upload/campaigns/image', hasBody: true }, { body: { url } })
  }

  async getMyEmailPreference(): Promise<{ scope: OptOutScope | null }> {
    return this.ctx.call({ httpMethod: 'GET', path: '/members/me/email-preferences' })
  }

  async setMyEmailPreference(scope: OptOutScope | null): Promise<{ scope: OptOutScope | null }> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/members/me/email-preferences', hasBody: true }, { body: { scope } })
  }
}
